import type { Maybe } from '@adornis/base/utilTypes.js';
import { ID } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import type { PartialEntityData } from '@adornis/baseql/entities/types.js';
import { registerEnum } from '@adornis/baseql/metadata/register.js';

export enum TemplateFieldType {
  TEXT = 'TEXT',
  NUMBER = 'NUMBER',
  DATE = 'DATE',
  BOOLEAN = 'BOOLEAN',
}

registerEnum({ name: 'TemplateFieldType', enum: TemplateFieldType });

@Entity()
export class TemplateField extends MongoEntity {
  static override _class = 'TemplateField';
  static override _collectionName = 'template-fields';

  constructor(doc: PartialEntityData<TemplateField>) {
    super(doc);
  }

  /**
   * The organization that owns this field. The extraction catalog is built from
   * exactly one organization's fields, so this also decides which tokens the LLM
   * is asked about.
   */
  @Field(() => ID)
  organizationId: Maybe<string>;

  /**
   * The token to be used in the template. Defines the placeholder in the template.
   * Example: {{firstName}} or {{lastName}}
   */
  @Field(() => String)
  token: Maybe<string>;

  /**
   * A description of the field. This is used by the AI to understand what it is searching for
   */
  @Field(() => String)
  description: Maybe<string>;

  /**
   * The type of the field. This is used to validate the field value and tells the AI how to interpret the value.
   */
  @Field(() => TemplateFieldType)
  type: Maybe<TemplateFieldType>;

  /**
   * Whether the field is an array of the specified type. The AI will search for multiple values of this type.
   * Will be parsed with index to the templater
   */
  @Field(() => Boolean, { default: v => v ?? false })
  arrayType: Maybe<boolean>;

  /**
   * Set by the ERM archive action — a non-null value means the field is archived.
   * Optional so existing data (e.g. the legacy migration) doesn't need to set it.
   * Lets the entity be managed by `@adornis/entity-relationship-management`.
   */
  @Field(() => Date)
  archivedAt?: Maybe<Date>;
}

/**
 * The only fields `updateTemplateField` takes from the caller.
 *
 * `organizationId` is deliberately absent: ownership is decided when the field
 * is created and is what every permission check reads, so a save must not be
 * able to move a field into an organization the caller has no authority over.
 * `archivedAt` moves only through the archive endpoint.
 */
export const TEMPLATE_FIELD_EDITABLE_FIELDS = [
  'token',
  'description',
  'type',
  'arrayType',
] as const satisfies ReadonlyArray<keyof TemplateField>;
